import { Outlet } from 'react-router-dom';
import { useState, type MouseEvent } from 'react';
import AppNavigation from '../components/layouts/AppNavigation';
import { useAuthStore } from '../store/authStore';
import { logout } from '../services/authService';
import ApexForgeScriptLoader from '../components/common/ApexForgeScriptLoader';
import { AnnouncementBanner } from '../components/common/AnnouncementBanner';
import { VersionInfo } from '../components/common/VersionInfo';
import http from '@/services/http';
import { toast } from 'react-hot-toast';

interface AppLayoutProps {
    hideHeader?: boolean;
    logoUrl?: string | null;
}

export default function AppLayout({ hideHeader = false, logoUrl }: AppLayoutProps) {
    const { user, clearUser, setInitialized } = useAuthStore();
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const handleLogout = async (event: MouseEvent<HTMLAnchorElement>) => {
        if (isLoggingOut) {
            return;
        }

        if (user?.impersonation) {
            event.preventDefault();
            setIsLoggingOut(true);

            try {
                const response = await http.post<{ redirect_url?: string }>('/admin/impersonate/stop');
                clearUser();
                setInitialized(false);
                window.location.href = response.data.redirect_url || '/dashboard';
            } catch (error) {
                console.error('Failed to stop impersonation', error);
                toast.error('Could not return to your account. Please try again.');
                setIsLoggingOut(false);
            }

            return;
        }

        setIsLoggingOut(true);
        logout('/project-logout', true);
    };
    return (
        <div className="min-h-screen bg-slate-100">
            <ApexForgeScriptLoader />
            {!hideHeader && (
                <AppNavigation
                    userName={user?.name ?? 'User'}
                    onLogout={handleLogout}
                    isImpersonating={Boolean(user?.impersonation)}
                    isLoggingOut={isLoggingOut}
                    initialLogoUrl={logoUrl}
                />
            )}

            {user && <AnnouncementBanner />}

            <div className="flex min-h-[calc(100vh-48px)] flex-col">
                <div className="mx-auto w-full px-5 py-4">
                    <Outlet />
                </div>
            </div>

            <footer className="fixed bottom-4 left-4 z-30 rounded-full bg-sky-400 text-white">
                <VersionInfo />
            </footer>
        </div>
    );
}
